import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  FlaskConical, Code, Copy, Check, Play, Loader2, AlertTriangle,
  ChevronDown, ChevronRight, X, Eye, EyeOff, Hash, Clock, Globe,
  ShieldCheck, FileText, Wand2, Settings2, ExternalLink,
  ArrowRight, Square, Shield, KeyRound, Zap, Upload, Image as ImageIcon,
  Code2, Sparkles, Camera, MessageSquare, Gamepad2,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge, Dot } from '../components/Badge';
import { SectionHeader } from '../components/Tabs';
import { apiFetch } from '../api';
import { formatNumber, copyToClipboard } from '../lib/utils';
import { cn } from '../lib/utils';

// =============================================================================
// Client-side cost estimator. Mirrors the backend's per-type cost table
// (selector.py + per-domain pipeline modules). When the backend's cost
// for a type changes we update the matching number here too — there is
// no central pricing endpoint; the playground is the canonical place
// users see "what will this cost me" before they hit Test.
//
// 2026-07-13: hCaptcha pricing is now split — Steam and similar
// per-domain pipelines cost 4 credits, the generic Chrome-for-Testing
// + NopeCHA + residential proxy pipeline costs 5 credits. The
// playground can't know which URL the user will paste, so we show the
// generic 5-credit number (the worst case) and link to the per-domain
// pricing rules in the docs.
// =============================================================================
const TYPE_COSTS = {
  recaptcha_v2_proxyless: 4,
  recaptcha_v2_proxy:     4,
  recaptcha_v3:           4,
  hcaptcha:               5,  // 2026-07-13: 5 (generic). Steam URLs are 4 — see docs.
  turnstile:              4,
  geetest:                4,
  imageToText:            1,
};

// Same 7 types the backend exposes in TYPE_RULES.
const CAPTCHA_TYPES = [
  { key: 'recaptcha_v2_proxyless', label: 'reCAPTCHA v2 (no proxy)', short: 'v2 proxyless', icon: ShieldCheck },
  { key: 'recaptcha_v2_proxy',     label: 'reCAPTCHA v2 (with proxy)', short: 'v2 proxy',    icon: Shield },
  { key: 'recaptcha_v3',           label: 'reCAPTCHA v3',             short: 'v3',          icon: Hash },
  { key: 'hcaptcha',               label: 'hCaptcha',                 short: 'hCaptcha',    icon: ShieldCheck },
  { key: 'turnstile',              label: 'Cloudflare Turnstile',     short: 'Turnstile',   icon: Shield },
  { key: 'geetest',                label: 'Geetest (v3 / v4)',        short: 'Geetest',     icon: Hash },
  { key: 'imageToText',            label: 'Image captcha (OCR)',      short: 'imageToText', icon: ImageIcon },
];

// 2026-07-13: quick-fill presets for the two per-domain hCaptcha
// pipelines that have a custom flow. Clicking one auto-sets the
// site_url (and known sitekey) so the user doesn't have to paste
// the URL from another tab to test the per-domain solver. Only
// shown when ``req.type === 'hcaptcha'`` because that's the only
// type where per-domain pipelines live today.
//
// Pricing is sourced from the docs page — keep in sync with
// ``PublicDocsPage.jsx`` and the backend selector.py. Steam stays
// 4 (per-domain override). Discord is 5 (generic rate). The
// ``credits`` field drives the badge text; users see the same
// number the backend charges.
const PRESETS = [
  {
    key: 'steam_signup',
    label: 'Steam (sign up)',
    sub: '4 credits',
    site_url: 'https://store.steampowered.com/join/',
    site_key: '',  // Steam pipeline ignores site_key
    icon: Gamepad2,
    iconBg: 'bg-[#1B2838]/10 text-[#1B2838]',
  },
  {
    key: 'discord_signup',
    label: 'Discord (sign up)',
    sub: '5 credits',
    site_url: 'https://discord.com/register',
    // 2026-07-13: sitekey was confirmed by an ad-hoc probe. Discord
    // inlines it in the React bundle and DOES NOT need this field
    // for the per-domain pipeline to work (NopeCHA discovers it),
    // but pre-filling lets the playground's "Code" tab show a
    // realistic curl/payload example with a non-empty sitekey.
    site_key: '3df4bdb784357f5655e38823330689144c50ab1f',
    icon: MessageSquare,
    iconBg: 'bg-[#5865F2]/10 text-[#5865F2]',
  },
];

// Per-type required + optional fields. Mirror of backend TYPE_RULES.
const TYPE_FIELDS = {
  recaptcha_v2_proxyless: {
    required: ['site_url', 'site_key'],
    optional: ['invisible', 'data_s', 'page_action', 'api_domain', 'user_agent', 'cookies'],
  },
  recaptcha_v2_proxy: {
    required: ['site_url', 'site_key', 'proxy_type', 'proxy_address', 'proxy_port'],
    optional: ['proxy_login', 'proxy_password', 'invisible', 'data_s', 'page_action', 'api_domain', 'user_agent', 'cookies'],
  },
  recaptcha_v3: {
    required: ['site_url', 'site_key'],
    optional: ['action', 'min_score', 'enterprise', 'api_domain', 'user_agent', 'cookies'],
  },
  hcaptcha: {
    // 2026-07-13: site_key is now OPTIONAL. The new endpoint
    // POST /captcha/hcaptcha accepts a body with just site_url —
    // the generic Chrome-for-Testing pipeline discovers the key
    // from the page itself. Steam URLs still work either way
    // (the Steam pipeline doesn't read site_key).
    required: ['site_url'],
    optional: ['site_key', 'invisible', 'user_agent', 'cookies'],
  },
  turnstile: {
    required: ['site_url', 'site_key'],
    optional: ['action', 'user_agent', 'cookies'],
  },
  geetest: {
    required: ['site_url'],
    optional: ['gt', 'challenge', 'captcha_id', 'geetest_version', 'user_agent', 'cookies'],
  },
  imageToText: {
    required: ['image'],
    optional: [],
  },
};

const DEFAULT_REQUEST = {
  type: 'turnstile',
  // Common
  site_url: '',
  site_key: '',
  // Proxy (recaptcha_v2_proxy)
  proxy_type: 'http',
  proxy_address: '',
  proxy_port: '',
  proxy_login: '',
  proxy_password: '',
  // reCAPTCHA extras
  invisible: false,
  data_s: '',
  page_action: '',
  api_domain: '',
  // reCAPTCHA v3
  action: '',
  min_score: 0.5,
  enterprise: false,
  // Geetest
  gt: '',
  challenge: '',
  captcha_id: '',
  version: 'v3',
  // (renamed to geetest_version on the wire — see cleanPayload)
  // Worker overrides
  user_agent: '',
  cookies: '',
  // imageToText
  image: '',
  image_filename: '',
  // Common
  timeout_ms: 60000,
};

const LANGS = [
  { key: 'curl',       label: 'cURL' },
  { key: 'python',     label: 'Python (requests)' },
  { key: 'python_aio', label: 'Python (aiohttp)' },
  { key: 'node',       label: 'Node.js (fetch)' },
  { key: 'node_axios', label: 'Node.js (axios)' },
  { key: 'ruby',       label: 'Ruby' },
  { key: 'go',         label: 'Go' },
  { key: 'php',        label: 'PHP' },
  { key: 'java',       label: 'Java' },
  { key: 'csharp',     label: 'C#' },
  { key: 'rust',       label: 'Rust' },
  { key: 'shell',      label: 'Shell' },
];

// Strip empty / null values from the request before POSTing, so we don't
// send a noisy payload full of blank fields. Also drops UI-only fields
// (timeout_ms, image_filename) and any field that isn't in the current
// type's required∪optional set.
//
// IMPORTANT: this walks TYPE_FIELDS (the single source of truth for the
// per-type schema) instead of using a static allow/deny list. That keeps
// it in sync with the backend's TYPE_RULES — when the backend adds a
// field we just add it to TYPE_FIELDS here and cleanPayload picks it up.
function cleanPayload(req) {
  const fields = TYPE_FIELDS[req.type];
  if (!fields) return { type: req.type };
  // Union of required + optional fields for this type. Anything in the
  // form state that's NOT in this set is either:
  //   - UI-only (timeout_ms, image_filename) — never sent
  //   - cross-typed (site_url on imageToText, min_score on turnstile) —
  //     also never sent
  const validForType = new Set([...fields.required, ...fields.optional]);

  const out = { type: req.type };
  for (const [k, v] of Object.entries(req)) {
    if (k === 'type') continue;
    if (k === 'timeout_ms') continue;            // UI-only
    if (k === 'image_filename') continue;        // UI-only (base64 lives in `image`)
    if (!validForType.has(k)) continue;          // not valid for this type
    // `version` is the UI key for geetest — sent on the wire as
    // `geetest_version` and only when type is geetest.
    if (k === 'version') {
      if (req.type !== 'geetest') continue;
      if (v === '' || v === null || v === undefined) continue;
      out.geetest_version = v;
      continue;
    }
    // skip empty / null / undefined / false booleans
    if (v === '' || v === null || v === undefined) continue;
    if (typeof v === 'boolean' && v === false) continue;
    out[k] = v;
  }
  return out;
}

// Convenience: list the field names that are still missing for the
// current type, given the request state. Empty array means "ready".
// Used by the Snapshot block + the missing-field error.
function missingFields(req) {
  const fields = TYPE_FIELDS[req.type];
  if (!fields) return [];
  return fields.required.filter((f) => {
    const v = req[f];
    if (req.type === 'imageToText' && f === 'image') return !v;
    return v === '' || v === null || v === undefined;
  });
}

// =============================================================================
// CaptchaPlaygroundPage — type-aware builder on the left, code+result on the right.
// UI mirrors /playground (fetch) so the two pages feel like siblings.
// =============================================================================
export default function CaptchaPlaygroundPage() {
  // --- Request state ---
  const [req, setReq] = useState(DEFAULT_REQUEST);
  const update = (patch) => setReq((r) => ({ ...r, ...patch }));

  // Switch type: keep shared fields (site_url, site_key), drop type-specific
  // empties so the new form starts clean.
  function switchType(newType) {
    setReq((r) => {
      const next = { ...DEFAULT_REQUEST, type: newType };
      // Preserve site_url/site_key if the new type uses them
      if (TYPE_FIELDS[newType].required.includes('site_url')) next.site_url = r.site_url;
      if (TYPE_FIELDS[newType].required.includes('site_key')) next.site_key = r.site_key;
      // Preserve image (some users may want to switch between imageToText and re-types
      // while keeping their data — but the new form will hide it if not applicable)
      next.image = r.image;
      next.image_filename = r.image_filename;
      return next;
    });
  }

  // --- API key (auto-fetched on mount, no rotation) ---
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keyNote, setKeyNote] = useState('');
  const [keyLoading, setKeyLoading] = useState(true);

  // --- Billing (for the cost-vs-balance warning at the top of run-test) ---
  // The route doesn't pass a `billing` prop so we fetch it once on mount,
  // mirroring what OverviewPage does. If the user isn't logged in or the
  // endpoint 403s we silently fall back to "balance unknown" and skip the
  // warning — never block the test on a balance fetch.
  const [creditBalance, setCreditBalance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const jwt = sess?.session?.access_token;
        if (!jwt) {
          if (!cancelled) {
            setKeyNote('Sign in to auto-fetch your API key.');
            setKeyLoading(false);
          }
          return;
        }
        let data;
        try {
          data = await apiFetch('/v1/keys/active-plaintext', {
            headers: { 'x-user-token': jwt },
          });
        } catch (e) {
          if (e?.status === 404) {
            if (!cancelled) {
              setKeyNote('No active API key. Create one from the API Keys page.');
              setKeyLoading(false);
            }
            return;
          }
          throw e;
        }
        if (!cancelled) {
          setApiKey(data.plaintext_key || '');
          setKeyNote(`Using your active key (created ${new Date(data.created_at).toLocaleDateString()}). No rotation performed.`);
          setKeyLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setKeyNote(`Could not auto-fetch key: ${e.message}. You can paste it below.`);
          setKeyLoading(false);
        }
      }
      // Billing fetch — best-effort. Failure is silent and just leaves the
      // cost pill in its default (no balance warning) state.
      try {
        const billing = await apiFetch('/v1/billing');
        if (!cancelled && typeof billing?.credit_balance === 'number') {
          setCreditBalance(billing.credit_balance);
        }
      } catch {
        // ignore — playground still works without a balance indicator
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- Response state ---
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);

  // --- UI state ---
  const [lang, setLang] = useState('python');
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  // --- Cost (constant per type — no extras) ---
  const cost = TYPE_COSTS[req.type] ?? 1;
  // costTooHigh: true when we have a balance and it's not enough for this
  // type. Used both to flip the cost pill to destructive and to surface an
  // inline warning. We leave the request enabled — the server is the
  // source of truth — but make the consequence visible before the click.
  const costTooHigh =
    creditBalance != null && typeof creditBalance === 'number' && creditBalance < cost;

  // --- Code snippet ---
  const snippet = useMemo(() => buildSnippet(req, lang, apiKey), [req, lang, apiKey]);

  // Reset result on type change / image change
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [req.type, req.site_url, req.site_key, req.image]);

  // --- Snapshot (live payload preview, mirrors cleanPayload) ---
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const snapshotBody = useMemo(() => cleanPayload(req), [req]);
  const snapshotMissing = useMemo(() => missingFields(req), [req]);

  // ===========================================================================
  // Handlers
  // ===========================================================================
  async function runTest() {
    const missing = missingFields(req);
    if (missing.length > 0) {
      setError(`Missing required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    setElapsedMs(null);
    const t0 = performance.now();
    try {
      const body = cleanPayload(req);
      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['x-api-key'] = apiKey;
      // 2026-07-12: hCaptcha moved to a dedicated endpoint. The generic
      // POST /captcha now returns 400 ``captcha_endpoint_moved`` for
      // ``type=hcaptcha`` (the recognition-based pipeline was deleted
      // 2026-07). The dedicated POST /captcha/hcaptcha accepts the
      // same shape (site_url, site_key, optional invisible/cookies/etc.)
      // and returns the new vendor-neutral response (status, token,
      // took_ms, credits_used, strategy).
      const endpoint = req.type === 'hcaptcha' ? '/captcha/hcaptcha' : '/captcha';
      const parsed = await apiFetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      setResult(parsed);
    } catch (e) {
      // apiFetch throws Error with .detail (parsed body) and .status.
      // FastAPI/Pydantic 422 returns detail as [{ type, loc, msg }, ...].
      // Format those into a readable list of bad fields.
      let msg = e.message || String(e);
      if (Array.isArray(e?.detail)) {
        const fields = e.detail
          .map((d) => {
            const path = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
            return path ? `${path}: ${d.msg}` : d.msg;
          })
          .filter(Boolean);
        if (fields.length) msg = `Server rejected the payload: ${fields.join('; ')}`;
      }
      setError(msg);
    } finally {
      setElapsedMs(Math.round(performance.now() - t0));
      setBusy(false);
    }
  }

  async function copySnippet() {
    const ok = await copyToClipboard(snippet);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  function resetRequest() {
    setReq(DEFAULT_REQUEST);
    setResult(null);
    setError(null);
  }

  // --- File -> base64 ---
  async function onPickImage(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      // Strip data URL prefix if present
      const base64 = typeof dataUrl === 'string' && dataUrl.includes(',')
        ? dataUrl.split(',')[1]
        : dataUrl;
      update({ image: base64, image_filename: file.name });
    };
    reader.readAsDataURL(file);
  }

  // ===========================================================================
  // Render
  // ===========================================================================
  const typeFields = TYPE_FIELDS[req.type];
  const showSiteKey = typeFields.required.includes('site_key');

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Captcha Playground"
        description="Solve a captcha on demand. Pick a type, fill in the site URL + sitekey, and test it live."
      />

      {/* API key card */}
      <Card>
        <Card.Body className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <KeyRound className="h-4 w-4 text-brand" />
              <span className="text-xs font-medium text-fg">API key</span>
              {keyLoading ? (
                <span className="inline-flex items-center gap-1 text-2xs text-fg-muted">
                  <Loader2 className="h-3 w-3 animate-spin" /> loading…
                </span>
              ) : apiKey ? (
                <Badge variant="success">
                  <Dot variant="success" /> Active
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <Dot variant="destructive" /> No key
                </Badge>
              )}
            </div>
            <div className="relative flex-1">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={keyLoading ? 'loading…' : 'sk_...'}
                spellCheck={false}
                autoComplete="off"
                className="w-full h-9 pl-3 pr-10 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
                title={showKey ? 'Hide' : 'Show'}
              >
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <span className="text-2xs text-fg-subtle shrink-0">
              <a href="/keys" className="text-brand hover:underline">API Keys</a>
            </span>
          </div>
          {keyNote && (
            <div className={cn(
              'mt-2.5 px-2.5 py-1.5 rounded-md text-2xs flex items-start gap-1.5',
              apiKey
                ? 'bg-success-soft border border-success/20 text-success-fg'
                : 'bg-warning-soft border border-warning/20 text-warning-fg',
            )}>
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
              <span>{keyNote}</span>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ============================ LEFT: BUILDER ============================ */}
        <Card className="lg:col-span-2">
          <Card.Header>
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-brand" />
                <Card.Title>Request</Card.Title>
              </div>
              <button
                type="button"
                onClick={resetRequest}
                className="text-2xs text-fg-muted hover:text-fg transition-colors"
              >
                Reset
              </button>
            </div>
          </Card.Header>
          <Card.Body className="space-y-5">
            {/* --- Type picker --- */}
            <Section label="Captcha type" icon={Shield}>
              {/* Mobile: single-row horizontal scroll strip.
                  Tablet+: 2-col / 3-col grid (preserves the existing
                  responsiveness on larger screens). The strip has touch
                  momentum on iOS via `overflow-x-auto`. Active type carries
                  the brand-soft fill so the chosen option stays obvious
                  when the user is mid-scroll. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {CAPTCHA_TYPES.map((t) => (
                  <TypeOption
                    key={t.key}
                    active={req.type === t.key}
                    onClick={() => switchType(t.key)}
                    label={t.short}
                    sub={TYPE_COSTS[t.key] + ' credits'}
                    Icon={t.icon}
                  />
                ))}
              </div>
            </Section>

            {/* --- Quick presets (hCaptcha only) ------------------------
                2026-07-13: Per-domain pipelines (Steam, Discord) need
                the operator to paste a specific site_url. These two
                preset buttons auto-fill the form for the two known
                per-domain flows. Hidden for non-hCaptcha types because
                those don't have per-domain pipelines today. */}
            {req.type === 'hcaptcha' && (
              <Section label="Quick presets" icon={Sparkles}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        update({ site_url: p.site_url, site_key: p.site_key });
                        setAdvancedOpen(true);
                      }}
                      className="group flex items-center gap-3 p-3 rounded-md bg-surface border border-border hover:border-brand hover:bg-brand/5 transition-colors text-left"
                      aria-label={`Fill form with ${p.label}`}
                    >
                      <div className={cn(
                        "h-9 w-9 flex items-center justify-center rounded-md shrink-0",
                        p.iconBg,
                      )}>
                        <p.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-fg">
                          {p.label}
                        </div>
                        <div className="text-2xs text-fg-muted font-mono truncate">
                          {p.site_url}
                        </div>
                      </div>
                      <Badge variant="default" className="shrink-0 text-2xs">
                        {p.sub}
                      </Badge>
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* --- Image upload (imageToText only) --- */}
            {req.type === 'imageToText' && (
              <Section label="Image" icon={ImageIcon}>
                <Field
                  label="Captcha image"
                  hint="PNG, JPEG, or WEBP. Max ~10MB."
                  required
                >
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-surface border border-border text-xs text-fg-muted hover:text-fg cursor-pointer transition-colors">
                      <Upload className="h-3.5 w-3.5" />
                      {req.image_filename || 'Choose file…'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => onPickImage(e.target.files?.[0])}
                      />
                    </label>
                    {req.image && (
                      <button
                        type="button"
                        onClick={() => update({ image: '', image_filename: '' })}
                        className="inline-flex items-center gap-1 h-9 px-2 rounded-md text-2xs text-fg-muted hover:text-fg transition-colors"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  {req.image && (
                    <div className="mt-2 p-2 rounded-md bg-surface-2 border border-border">
                      <img
                        src={`data:image/png;base64,${req.image}`}
                        alt="captcha"
                        className="max-h-32 mx-auto rounded"
                      />
                      <div className="mt-1 text-2xs text-fg-muted font-mono text-center">
                        {req.image.length.toLocaleString()} chars (base64)
                      </div>
                    </div>
                  )}
                </Field>
              </Section>
            )}

            {/* --- Site info --- */}
            {req.type !== 'imageToText' && (
              <Section label="Site" icon={Globe}>
                <Field label="Site URL" hint="The URL where the captcha is embedded" required>
                  <input
                    type="url"
                    value={req.site_url}
                    onChange={(e) => update({ site_url: e.target.value })}
                    placeholder=""
                    className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40"
                  />
                </Field>
                {showSiteKey && (
                  <Field label="Site key" hint="The public sitekey from the page" required>
                    <input
                      type="text"
                      value={req.site_key}
                      onChange={(e) => update({ site_key: e.target.value })}
                      placeholder={req.type === 'turnstile' ? '0x4AAAAAAA...' : req.type === 'hcaptcha' ? 'a-uuid-here' : '6Lc...AbC'}
                      className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
                    />
                  </Field>
                )}
              </Section>
            )}

            {/* --- Proxy (recaptcha_v2_proxy only) --- */}
            {req.type === 'recaptcha_v2_proxy' && (
              <Section label="Proxy" icon={Shield}>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Type">
                    <select
                      value={req.proxy_type}
                      onChange={(e) => update({ proxy_type: e.target.value })}
                      className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                    >
                      <option value="http">http</option>
                      <option value="socks4">socks4</option>
                      <option value="socks5">socks5</option>
                    </select>
                  </Field>
                  <Field label="Host">
                    <input
                      type="text"
                      value={req.proxy_address}
                      onChange={(e) => update({ proxy_address: e.target.value })}
                      placeholder="127.0.0.1"
                      className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
                    />
                  </Field>
                  <Field label="Port">
                    <input
                      type="number"
                      value={req.proxy_port}
                      onChange={(e) => update({ proxy_port: parseInt(e.target.value) || '' })}
                      placeholder="1080"
                      className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Username (optional)">
                    <input
                      type="text"
                      value={req.proxy_login}
                      onChange={(e) => update({ proxy_login: e.target.value })}
                      className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                    />
                  </Field>
                  <Field label="Password (optional)">
                    <input
                      type="password"
                      value={req.proxy_password}
                      onChange={(e) => update({ proxy_password: e.target.value })}
                      className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                    />
                  </Field>
                </div>
              </Section>
            )}

            {/* --- Optional fields --- */}
            {typeFields.optional.length > 0 && (
              <Section label="Optional fields" icon={Settings2}>
                {/* reCAPTCHA v2 / Enterprise */}
                {(req.type === 'recaptcha_v2_proxyless' || req.type === 'recaptcha_v2_proxy') && (
                  <>
                    <Toggle
                      label="Invisible"
                      description="The V2 Invisible variant (no checkbox shown)"
                      checked={req.invisible}
                      onChange={(v) => update({ invisible: v })}
                    />
                    <Field label="data_s (Enterprise only)" hint="The recaptcha-data-s value from the page">
                      <input
                        type="text"
                        value={req.data_s}
                        onChange={(e) => update({ data_s: e.target.value })}
                        className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      />
                    </Field>
                    <Field label="page_action (Enterprise only)">
                      <input
                        type="text"
                        value={req.page_action}
                        onChange={(e) => update({ page_action: e.target.value })}
                        placeholder="submit"
                        className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      />
                    </Field>
                    <Field label="api_domain (override)" hint='e.g. "google.com", "recaptcha.net"'>
                      <input
                        type="text"
                        value={req.api_domain}
                        onChange={(e) => update({ api_domain: e.target.value })}
                        className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      />
                    </Field>
                  </>
                )}

                {/* reCAPTCHA v3 */}
                {req.type === 'recaptcha_v3' && (
                  <>
                    <Field label="action" hint="Submit action name (e.g. login, submit)">
                      <input
                        type="text"
                        value={req.action}
                        onChange={(e) => update({ action: e.target.value })}
                        placeholder="submit"
                        className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      />
                    </Field>
                    <Field label="min_score" hint="Minimum acceptable score (0.1 - 0.9)">
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="0.9"
                        value={req.min_score}
                        onChange={(e) => update({ min_score: parseFloat(e.target.value) || 0.5 })}
                        className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      />
                    </Field>
                    <Toggle
                      label="Enterprise"
                      description="Use reCAPTCHA Enterprise endpoints"
                      checked={req.enterprise}
                      onChange={(v) => update({ enterprise: v })}
                    />
                  </>
                )}

                {/* hCaptcha */}
                {req.type === 'hcaptcha' && (
                  <Toggle
                    label="Invisible"
                    description="hCaptcha Invisible variant"
                    checked={req.invisible}
                    onChange={(v) => update({ invisible: v })}
                  />
                )}

                {/* Turnstile */}
                {req.type === 'turnstile' && (
                  <Field label="action" hint="Optional Cloudflare action name">
                    <input
                      type="text"
                      value={req.action}
                      onChange={(e) => update({ action: e.target.value })}
                      placeholder="submit"
                      className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                    />
                  </Field>
                )}

                {/* Geetest */}
                {req.type === 'geetest' && (
                  <>
                    <Field label="Version">
                      <select
                        value={req.version}
                        onChange={(e) => update({ version: e.target.value })}
                        className="w-full h-9 px-2 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                      >
                        <option value="v3">v3 (gt + challenge)</option>
                        <option value="v4">v4 (captcha_id)</option>
                      </select>
                    </Field>
                    {req.version === 'v3' ? (
                      <>
                        <Field label="gt" hint="Geetest v3 gt identifier">
                          <input
                            type="text"
                            value={req.gt}
                            onChange={(e) => update({ gt: e.target.value })}
                            className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                          />
                        </Field>
                        <Field label="challenge" hint="Geetest v3 challenge string">
                          <input
                            type="text"
                            value={req.challenge}
                            onChange={(e) => update({ challenge: e.target.value })}
                            className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                          />
                        </Field>
                      </>
                    ) : (
                      <Field label="captcha_id" hint="Geetest v4 captcha_id">
                        <input
                          type="text"
                          value={req.captcha_id}
                          onChange={(e) => update({ captcha_id: e.target.value })}
                          className="w-full h-9 px-3 text-sm font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                        />
                      </Field>
                    )}
                  </>
                )}

                {/* Worker overrides (common optional) */}
                <Field label="user_agent (optional)" hint="Override the worker browser UA">
                  <input
                    type="text"
                    value={req.user_agent}
                    onChange={(e) => update({ user_agent: e.target.value })}
                    placeholder="Mozilla/5.0 ..."
                    className="w-full h-9 px-3 text-xs font-mono bg-bg border border-border rounded-md text-fg placeholder:text-fg-subtle focus:outline-none focus:border-brand"
                  />
                </Field>
                <Field label="cookies (optional)" hint='Semicolon-joined, e.g. "k1=v1; k2=v2"'>
                  <input
                    type="text"
                    value={req.cookies}
                    onChange={(e) => update({ cookies: e.target.value })}
                    className="w-full h-9 px-3 text-xs font-mono bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                  />
                </Field>
              </Section>
            )}

            {/* --- Advanced --- */}
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-1.5 text-2xs font-medium text-fg-muted hover:text-fg transition-colors w-full"
            >
              {advancedOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Advanced
            </button>
            {advancedOpen && (
              <Section label="Advanced" icon={Settings2}>
                <Field label="Timeout (ms)" hint="1000-120000">
                  <input
                    type="number"
                    min="1000"
                    max="120000"
                    step="1000"
                    value={req.timeout_ms}
                    onChange={(e) => update({ timeout_ms: parseInt(e.target.value) || 60000 })}
                    className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                  />
                </Field>
              </Section>
            )}
          </Card.Body>
        </Card>

        {/* ============================ RIGHT: CODE + RESULT ============================ */}
        <div className="lg:col-span-3 space-y-4">
          {/* Cost + readiness bar */}
          <Card>
            <Card.Body className="p-4 space-y-3">
              {/* Row 1: required-fields hint + cost + balance + button */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-1.5">
                  <Badge variant={costTooHigh ? 'destructive' : 'warning'}>
                    {cost} credit{cost !== 1 ? 's' : ''}
                  </Badge>
                  <span className="text-2xs text-fg-muted">per attempt</span>
                </div>
                {creditBalance != null && (
                  <span className="text-2xs text-fg-muted">
                    Balance: <span className="font-mono font-semibold text-fg">{creditBalance.toLocaleString()}</span>
                  </span>
                )}
                <div className="flex-1" />
                <Button onClick={runTest} disabled={busy} className="px-5">
                  {busy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Test solve
                    </>
                  )}
                </Button>
              </div>

              {/* Row 2: readiness hint - what fields we still need */}
              <div className="flex items-start gap-2 text-2xs">
                {snapshotMissing.length === 0 ? (
                  <Badge variant="success">
                    <Dot variant="success" />
                    ready
                  </Badge>
                ) : (
                  <Badge variant="warning">
                    <Dot variant="warning" />
                    missing: {snapshotMissing.join(', ')}
                  </Badge>
                )}
                <span className="text-fg-muted">
                  requires: <span className="font-mono text-fg">{typeFields.required.join(', ')}</span>
                  {typeFields.optional.length > 0 && (
                    <> · optional: <span className="font-mono text-fg-subtle">{typeFields.optional.join(', ')}</span></>
                  )}
                </span>
              </div>

              {/* Row 3: balance-too-low warning (only when balance known and insufficient) */}
              {costTooHigh && (
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Your balance is{' '}
                    <span className="font-mono font-semibold">{creditBalance.toLocaleString()}</span>{' '}
                    {creditBalance === 1 ? 'credit' : 'credits'}; this{' '}
                    <span className="font-mono font-semibold">{TYPE_COSTS[req.type]}-credit</span>{' '}
                    test won't go through.{' '}
                    <a href="/topup" className="underline font-medium">Top up</a>.
                  </span>
                </div>
              )}

              {/* Error from the last attempt */}
              {error && (
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Row 4: snapshot of the payload we're about to POST */}
              <button
                type="button"
                onClick={() => setSnapshotOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-2xs text-fg-muted hover:text-fg transition-colors"
              >
                {snapshotOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {snapshotOpen ? 'Hide' : 'Show'} payload that will be POSTed
              </button>
              {snapshotOpen && (
                <pre className="mt-1 p-2.5 rounded-md bg-surface-2 border border-border font-mono text-2xs overflow-x-auto text-fg-muted">
                  {JSON.stringify(snapshotBody, null, 2)}
                </pre>
              )}
            </Card.Body>
          </Card>

          {/* Code preview */}
          <Card>
            <Card.Header className="flex-row items-center justify-between gap-2 space-y-0">
              <div className="flex items-center gap-2">
                <Code2 className="h-4 w-4 text-brand" />
                <Card.Title>Code</Card.Title>
              </div>
              <div className="flex items-center gap-2">
                <LangDropdown lang={lang} onChange={setLang} />
                <Button variant="secondary" size="sm" onClick={copySnippet}>
                  {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <CodeView snippet={snippet} lang={lang} />
            </Card.Body>
          </Card>

          {/* Result */}
          <Card>
            <Card.Header className="flex-col items-stretch gap-3 space-y-0 p-4">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-brand" />
                  <Card.Title>Result</Card.Title>
                  {result && !busy && (
                    <Badge variant="success">
                      <Dot variant="success" /> Solved
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {elapsedMs != null && !busy && (
                    <span className="text-2xs text-fg-muted">
                      Executed in <span className="font-mono font-semibold text-fg">{elapsedMs.toLocaleString()} ms</span>
                    </span>
                  )}
                  {result && result.credits_used != null && !busy && (
                    <Badge variant="default">
                      {result.credits_used} credit{result.credits_used !== 1 ? 's' : ''} withdrawn
                    </Badge>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {busy && (
                <div className="flex items-center gap-2 text-fg-muted text-sm py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Solving captcha…
                </div>
              )}
              {!busy && !result && !error && (
                <div className="text-center py-10 px-4 text-fg-muted text-sm">
                  <Sparkles className="h-6 w-6 mx-auto mb-2 text-brand opacity-60" />
                  <div className="text-fg font-medium">Ready when you are</div>
                  <div className="mt-1">
                    Fill in the{' '}
                    <span className="font-mono text-fg">{TYPE_FIELDS[req.type].required.join(' + ')}</span>{' '}
                    and click <strong className="text-fg">Test solve</strong>. This will withdraw{' '}
                    <span className="font-mono text-fg">{cost}</span> credit
                    {cost !== 1 ? 's' : ''} from your balance.
                  </div>
                </div>
              )}
              {result && (
                <div className="space-y-3">
                  {result.token && (
                    <TokenBox value={result.token} />
                  )}
                  {result.text && (
                    <div>
                      <div className="text-2xs uppercase tracking-wider text-fg-muted mb-1">OCR text</div>
                      <div className="font-mono text-xs bg-surface-2 border border-border rounded-md p-2.5 break-all text-fg">
                        {result.text}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    {result.status && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">status</div>
                        <div className="font-mono text-fg">{result.status}</div>
                      </div>
                    )}
                    {result.strategy && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">strategy</div>
                        <div className="font-mono text-fg">{result.strategy}</div>
                      </div>
                    )}
                    {result.type && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">type</div>
                        <div className="font-mono text-fg">{result.type}</div>
                      </div>
                    )}
                    {result.took_ms != null && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">took_ms</div>
                        <div className="font-mono text-fg">{result.took_ms.toLocaleString()}</div>
                      </div>
                    )}
                    {result.rounds_attempted != null && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">rounds</div>
                        <div className="font-mono text-fg">{result.rounds_attempted}</div>
                      </div>
                    )}
                    {result.solver_calls != null && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">calls</div>
                        <div className="font-mono text-fg">{result.solver_calls}</div>
                      </div>
                    )}
                    {result.recognition_calls != null && result.solver_calls == null && (
                      <div className="rounded-md bg-surface-2 border border-border p-2">
                        <div className="text-2xs uppercase tracking-wider text-fg-muted">calls</div>
                        <div className="font-mono text-fg">{result.recognition_calls}</div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowRaw((v) => !v)}
                    className="text-2xs text-fg-muted hover:text-fg transition-colors"
                  >
                    {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
                  </button>
                  {showRaw && (
                    <pre className="font-mono text-2xs bg-surface-2 border border-border rounded-md p-3 overflow-x-auto text-fg-muted">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Helpers — kept here to mirror PlaygroundPage's self-contained style.
// =============================================================================

// TokenBox — shows the returned captcha token with a one-click copy button.
// Replaces the older "just text in a div" treatment so the most-useful
// piece of the result is one click away from the user's clipboard.
function TokenBox({ value }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    const ok = await copyToClipboard(value || '');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-2xs uppercase tracking-wider text-fg-muted">
          Token
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={copy}
          aria-label="Copy token"
          title="Copy token to clipboard"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-success" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </Button>
      </div>
      <div className="font-mono text-xs bg-surface-2 border border-border rounded-md p-2.5 break-all text-fg">
        {value}
      </div>
    </div>
  );
}

function Section({ label, icon: Icon, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
        <Icon className="h-3.5 w-3.5 text-brand" />
        {label}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, required = false, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-2xs font-medium text-fg-muted">
        {required ? (
          <>
            {label}
            <span className="text-destructive ml-0.5" aria-label="required">*</span>
          </>
        ) : (
          <>{label}</>
        )}
        {hint && <span className="text-fg-subtle font-normal ml-1.5">— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'mt-0.5 shrink-0 inline-flex h-5 w-9 items-center rounded-full transition-colors',
          checked ? 'bg-brand' : 'bg-surface-2 border border-border',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          )}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-fg">{label}</div>
        {description && <div className="text-2xs text-fg-muted">{description}</div>}
      </div>
    </label>
  );
}

function TypeOption({ active, onClick, label, sub, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 p-2.5 rounded-lg border text-left transition-all',
        active
          ? 'border-brand bg-brand-softest ring-1 ring-brand/30'
          : 'border-border bg-surface hover:border-fg-subtle',
      )}
    >
      <div className="flex items-center gap-1.5">
        <Icon className={cn('h-3.5 w-3.5', active ? 'text-brand' : 'text-fg-muted')} />
        <span className={cn('text-xs font-semibold', active ? 'text-fg' : 'text-fg')}>{label}</span>
      </div>
      <span className="text-2xs text-fg-muted">{sub}</span>
    </button>
  );
}

function LangDropdown({ lang, onChange }) {
  const current = LANGS.find((l) => l.key === lang) || LANGS[1];
  return (
    <select
      value={lang}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 px-2 text-xs bg-surface border border-border rounded-md text-fg focus:outline-none focus:border-brand"
    >
      {LANGS.map((l) => (
        <option key={l.key} value={l.key}>{l.label}</option>
      ))}
    </select>
  );
}

// =============================================================================
// CodeView — syntax-highlighted monospace block. Same as the fetch playground's
// lightweight CodeView (no external lib, just background + monospace).
// =============================================================================
function CodeView({ snippet, lang }) {
  return (
    <div className="bg-surface-2 border-t border-border overflow-x-auto">
      <pre className="p-3 font-mono text-2xs leading-relaxed text-fg whitespace-pre">
        <code>{snippet}</code>
      </pre>
    </div>
  );
}

// =============================================================================
// Snippet builders — one per language. Keeps each small + readable.
// =============================================================================
function buildSnippet(req, lang, apiKey) {
  const body = JSON.stringify(cleanPayload(req), null, 2);
  // 2026-07-12: hCaptcha uses the dedicated /captcha/hcaptcha endpoint;
  // everything else still uses the generic /captcha alias.
  const url = req.type === 'hcaptcha'
    ? 'https://api.datames.dev/captcha/hcaptcha'
    : 'https://api.datames.dev/captcha';
  const headers = apiKey ? `  -H "x-api-key: ${apiKey} \\\n` : '';
    const ct = '  -H "Content-Type: application/json" \\\n';
    const dataLine = `  -d '${body.replace(/'/g, "'\\''")}'`;

  switch (lang) {
    case 'curl':
      return `curl -X POST ${url} \\\n${ct}${headers}${dataLine}`;

    case 'python': {
      const payload = JSON.stringify(cleanPayload(req));
      return `import requests\n\nresp = requests.post(\n    "${url}",\n    headers=${apiKey ? `{"x-api-key": "${apiKey}"}` : '{}'},\n    json=${payload},\n)\nprint(resp.status_code, resp.json())`;
    }

    case 'python_aio': {
      const payload = JSON.stringify(cleanPayload(req));
      return `import aiohttp, asyncio\n\nasync def solve():\n    async with aiohttp.ClientSession() as s:\n        async with s.post(\n            "${url}",\n            headers=${apiKey ? `{"x-api-key": "${apiKey}"}` : '{}'},\n            json=${payload},\n        ) as r:\n            print(r.status, await r.json())\n\nasyncio.run(solve())`;
    }

    case 'node': {
      const payload = JSON.stringify(cleanPayload(req));
      return `const body = ${payload};\n\nconst resp = await fetch("${url}", {\n  method: "POST",\n  headers: {\n    "Content-Type": "application/json"${apiKey ? `,\n    "x-api-key": "${apiKey}"` : ''},\n  },\n  body: JSON.stringify(body),\n});\nconsole.log(resp.status, await resp.json());`;
    }

    case 'node_axios': {
      const payload = JSON.stringify(cleanPayload(req));
      return `import axios from "axios";\n\nconst resp = await axios.post(\n  "${url}",\n  ${payload},\n  { headers: ${apiKey ? `{ "x-api-key": "${apiKey}" }` : '{}'} }\n);\nconsole.log(resp.status, resp.data);`;
    }

    case 'ruby': {
      const payload = JSON.stringify(cleanPayload(req));
      return `require "net/http"\nrequire "json"\nrequire "uri"\n\nuri = URI.parse("${url}")\nhttp = Net::HTTP.new(uri.host, uri.port)\nhttp.use_ssl = (uri.scheme == "https")\nreq = Net::HTTP::Post.new(uri.request_uri, { "Content-Type" => "application/json"${apiKey ? `, "x-api-key" => "${apiKey}"` : ''} })\nreq.body = ${payload}.to_json\nresp = http.request(req)\nputs resp.code, resp.body`;
    }

    case 'go': {
      const payload = JSON.stringify(cleanPayload(req));
      return `package main\n\nimport (\n    "bytes"\n    "encoding/json"\n    "fmt"\n    "net/http"\n)\n\nfunc main() {\n    body, _ := json.Marshal(${payload})\n    req, _ := http.NewRequest("POST", "${url}", bytes.NewReader(body))\n    req.Header.Set("Content-Type", "application/json")${apiKey ? `\n    req.Header.Set("x-api-key", "${apiKey}")` : ''}\n    resp, _ := http.DefaultClient.Do(req)\n    defer resp.Body.Close()\n    fmt.Println(resp.Status)\n}`;
    }

    case 'php': {
      const payload = JSON.stringify(cleanPayload(req));
      return `<?php\n$ch = curl_init("${url}");\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\ncurl_setopt($ch, CURLOPT_POST, true);\ncurl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(${payload}));\ncurl_setopt($ch, CURLOPT_HTTPHEADER, [\n    "Content-Type: application/json"${apiKey ? `,\n    "x-api-key: ${apiKey}"` : ''},\n]);\n$resp = curl_exec($ch);\necho $resp;`;
    }

    case 'java': {
      const payload = JSON.stringify(cleanPayload(req));
      return `import java.net.http.*;\nimport java.net.URI;\n\nHttpClient client = HttpClient.newHttpClient();\nHttpRequest req = HttpRequest.newBuilder()\n    .uri(URI.create("${url}"))\n    .header("Content-Type", "application/json")${apiKey ? `\n    .header("x-api-key", "${apiKey}")` : ''}\n    .POST(HttpRequest.BodyPublishers.ofString(${payload}))\n    .build();\nHttpResponse<String> resp = client.send(req, HttpResponse.BodyHandlers.ofString());\nSystem.out.println(resp.statusCode() + " " + resp.body());`;
    }

    case 'csharp': {
      const payload = JSON.stringify(cleanPayload(req));
      return `using System.Net.Http;\nusing System.Net.Http.Headers;\n\nvar client = new HttpClient();\nvar msg = new HttpRequestMessage(HttpMethod.Post, "${url}");\nmsg.Content = new StringContent(${payload});\nmsg.Content.Headers.ContentType = new MediaTypeHeaderValue("application/json");${apiKey ? `\nmsg.Headers.Add("x-api-key", "${apiKey}");` : ''}\nvar resp = await client.SendAsync(msg);\nConsole.WriteLine(resp.StatusCode + " " + await resp.Content.ReadAsStringAsync());`;
    }

    case 'rust': {
      const payload = JSON.stringify(cleanPayload(req));
      return `use reqwest::header::{HeaderMap, HeaderValue, CONTENT_TYPE};\n\n#[tokio::main]\nasync fn main() -> Result<(), Box<dyn std::error::Error>> {\n    let mut headers = HeaderMap::new();\n    headers.insert(CONTENT_TYPE, HeaderValue::from_static("application/json"));${apiKey ? `\n    headers.insert("x-api-key", HeaderValue::from_static("${apiKey}"));` : ''}\n    let client = reqwest::Client::new();\n    let resp = client.post("${url}")\n        .headers(headers)\n        .body(${payload})\n        .send().await?;\n    println!("{} {:?}", resp.status(), resp.text().await?);\n    Ok(())\n}`;
    }

    case 'shell':
      return `#!/usr/bin/env bash\nset -euo pipefail\n\ncurl -sS -X POST "${url}" \\\n  -H "Content-Type: application/json" \\\n${apiKey ? `  -H "x-api-key: ${apiKey}" \\\n` : ''}  -d '${body.replace(/'/g, "'\\''")}'`;

    default:
      return body;
  }
}